import type { ModelInfo, ModelUpdate } from '../shared/types';

export type Connection = 'api' | 'web' | 'mcp';

export function connectionOf(model: ModelInfo): Connection {
  return model.connection ?? (model.hasProvider ? 'api' : model.webUrl ? 'web' : 'mcp');
}

export function modelStatus(model: ModelInfo | undefined): string {
  if (!model) return '未配置';
  if (model.archived) return '已归档';
  const connection = connectionOf(model);
  if (connection === 'api') return model.hasProvider ? 'API 已配置' : '未配置';
  return connection === 'web' ? '网页共读' : 'MCP 共读';
}

export function modelSummary(model: ModelInfo): string {
  return [model.modelLabel, model.channel].filter(Boolean).join(' · ');
}

export function publicModelUpdate(model: ModelInfo): ModelUpdate {
  return {
    id: model.id,
    name: model.name,
    ...(model.modelLabel ? { modelLabel: model.modelLabel } : {}),
    ...(model.channel ? { channel: model.channel } : {}),
    connection: connectionOf(model),
    ...(model.webUrl ? { webUrl: model.webUrl } : {}),
    archived: !!model.archived,
  };
}

export interface ModelFields {
  id: string;
  name: string;
  modelLabel: string;
  channel: string;
  connection: Connection;
  baseUrl: string;
  apiModel: string;
  apiKey: string;
  webUrl: string;
  clearProvider: boolean;
}

function httpUrl(value: string, label: string): string {
  try {
    const url = new URL(value);
    if (url.protocol === 'https:' || url.protocol === 'http:') return value;
  } catch { /* Show one actionable validation message below. */ }
  throw new Error(`${label}需填写完整的 HTTP 或 HTTPS 地址`);
}

/** Build a write request without ever round-tripping a stored secret. */
export function modelUpdateFromFields(fields: ModelFields, previous?: ModelInfo): ModelUpdate {
  const name = fields.name.trim();
  if (!name) throw new Error('请填写实例名称');
  const update: ModelUpdate = {
    id: previous?.id ?? fields.id,
    name,
    modelLabel: fields.modelLabel.trim(),
    channel: fields.channel.trim(),
    connection: fields.connection,
    archived: !!previous?.archived,
  };
  const webUrl = fields.webUrl.trim();
  if (webUrl) update.webUrl = httpUrl(webUrl, '网页地址');
  if (fields.connection === 'web' && !webUrl) throw new Error('请填写模型网页版地址');
  if (fields.clearProvider) {
    update.provider = null;
  } else if (fields.connection === 'api') {
    const baseUrl = fields.baseUrl.trim();
    const model = fields.apiModel.trim();
    const apiKey = fields.apiKey.trim();
    const config = previous?.providerConfig;
    if (baseUrl || model || apiKey) {
      if (!baseUrl || !model) throw new Error('请同时填写 Base URL 和 API 模型 ID');
      httpUrl(baseUrl, 'Base URL');
      const sameEndpoint = config
        ? new URL(config.baseUrl).href.replace(/\/+$/, '') === new URL(baseUrl).href.replace(/\/+$/, '')
        : false;
      if (!apiKey && (!previous?.hasProvider || !sameEndpoint)) {
        throw new Error(previous?.hasProvider ? '更换 Base URL 时需重新输入 API Key' : '首次配置 API 时请输入 API Key');
      }
      if (apiKey || !sameEndpoint || model !== config?.model) {
        update.provider = { baseUrl, model, ...(apiKey ? { apiKey } : {}) };
      }
    }
  }
  return update;
}
